import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { ConsentModal } from './ConsentModal'
import { AIConsentModal } from './AIConsentModal'
import { BackfillModal } from './BackfillModal'
import { AgeRestrictedView } from './AgeRestrictedView'

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-slate-950">
        <div className="w-8 h-8 border-2 border-haven-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Age restriction takes precedence over everything else. A restricted
  // user (existing account that supplied an under-18 DOB in the backfill
  // modal) hits the locked view before any route or modal renders, so a
  // minor never slips into the app while something else loads.
  if (user.age_restricted) {
    return <AgeRestrictedView />
  }

  // New users who haven't completed onboarding get redirected to /onboarding
  // Check onboarding_completed_at first (new field), fall back to first_login_seen (legacy compat)
  const hasCompletedOnboarding = user.onboarding_completed_at || user.first_login_seen
  if (!hasCompletedOnboarding && location.pathname !== '/onboarding') {
    // The web Oura OAuth round-trip ends on /profile?oura=connected|error
    // (the backend's post-exchange redirect, app/api/oura.py). For a user
    // who connected Oura mid-onboarding, that signal is the only thing
    // telling Onboarding this is a *return* rather than a fresh start.
    // Dropping it made Onboarding wipe the saved draft and restart at
    // screen 1, so the user re-connected Oura and looped until they gave
    // up (prod, Sept 2026). Forward exactly that one param, nothing else.
    const ouraReturn = new URLSearchParams(location.search).get('oura')
    const search = ouraReturn ? `?oura=${encodeURIComponent(ouraReturn)}` : ''
    return <Navigate to={{ pathname: '/onboarding', search }} replace />
  }

  // If user has completed onboarding but somehow lands on /onboarding, redirect to dashboard
  if (hasCompletedOnboarding && location.pathname === '/onboarding') {
    return <Navigate to="/dashboard" replace />
  }

  // Demographics backfill gate (existing users predating the onboarding
  // demographics step). DOB is required, so this is surfaced BEFORE the
  // research-consent ask. Targets only post-onboarding users still
  // missing a DOB; brand-new users capture it during onboarding and are
  // redirected above before reaching here.
  const needsBackfill =
    hasCompletedOnboarding &&
    !user.date_of_birth &&
    location.pathname !== '/onboarding'

  // Research-consent gate. NULL means "not yet asked" — surface the
  // modal on top of the first app screen the user lands on after
  // onboarding. True/false both count as "already answered" and the
  // modal stays dismissed for the rest of the account's lifetime.
  // Only after demographics are captured (DOB present), so the two modals
  // never stack — demographics first, then consent.
  // AI data-sharing consent gate (5.1.1(i)/5.1.2(i)). NULL = never
  // asked under the new, Anthropic-naming disclosure — existing users
  // whose only prior ask was the research-framed modal must answer
  // this one. Surfaced BEFORE the research modal (the backend is
  // already failing closed for them, so this unblocks their AI
  // features), and never stacked with it.
  const needsAIConsent =
    hasCompletedOnboarding &&
    !needsBackfill &&
    user.ai_data_consent === null &&
    location.pathname !== '/onboarding'

  const needsConsent =
    hasCompletedOnboarding &&
    !needsBackfill &&
    !needsAIConsent &&
    user.research_consent === null &&
    location.pathname !== '/onboarding'

  return (
    <>
      {children}
      {needsBackfill && <BackfillModal />}
      {needsAIConsent && <AIConsentModal />}
      {needsConsent && <ConsentModal />}
    </>
  )
}
